<!--
  BulkReviewPanel — inline panel for bulk operations on multiple selected documents.

  Shown in the right column of Inbox (mode "review") and inside the Browse bulk modal
  (mode "edit") while documents are selected. All state mutation and API calls remain in the
  parent; this component owns the chip editor state and dispatches events upward.

  Three chips (assign / household / tags). A chip is either "not set" (neutral, field name) or
  "set" (accent, verb + value); its × resets it. Clicking a chip opens its editor directly below
  the chip row — at most one editor at a time. Only set chips end up in the parent's payload.

  Props:
    count               — number of currently selected documents
    mode                — "review": trigger accepts (Inbox); "edit": trigger only changes (Browse)
    members             — member list for the assign editor (active household)
    households          — all available households (for the household editor)
    bulkAssignMemberIds — two-way bound: selected member ids
    bulkHouseholdId     — two-way bound: chosen household id ('' = not set)
    bulkTags            — two-way bound: staged tags
    saving              — whether a bulk operation is in flight
    loading             — whether the page is loading

  Events:
    accept — trigger pressed; a pending, unconfirmed tag has been flushed into bulkTags first
    delete — "delete" chosen in the trigger's menu
-->
<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import {
    CheckCircle,
    Pencil,
    X,
    Users,
    Home,
    Tag,
    Plus,
    Trash2,
    ChevronDown,
  } from 'lucide-svelte';
  import { Badge } from './ui/index';
  import { memberRoleLabelKey } from '../memberRoles';
  import { mergeBulkTags } from '../bulkTags';

  export let count: number = 0;
  export let mode: 'review' | 'edit' = 'review';
  export let members: Array<{ id: string; name: string; role?: string }> = [];
  export let households: Array<{ id: string; name?: string; type?: string }> = [];

  export let bulkHouseholdId = '';
  export let bulkTags: string[] = [];
  export let bulkAssignMemberIds: string[] = [];

  export let saving = false;
  export let loading = false;

  type ChipKey = 'assign' | 'household' | 'tags';

  let newTag = '';
  let openEditor: ChipKey | null = null;
  // Context menu (destructive / secondary actions) next to the primary trigger.
  let menuOpen = false;
  let panelEl: HTMLDivElement;

  const dispatch = createEventDispatcher<{
    accept: void;
    delete: void;
  }>();

  function roleLabel(role: string): string {
    const key = memberRoleLabelKey(role);
    return key ? $_(key) : role;
  }

  function toggleEditor(key: ChipKey) {
    openEditor = openEditor === key ? null : key;
  }

  function toggleMember(memberId: string) {
    bulkAssignMemberIds = bulkAssignMemberIds.includes(memberId)
      ? bulkAssignMemberIds.filter((id) => id !== memberId)
      : [...bulkAssignMemberIds, memberId];
  }

  function handleAddTag() {
    const trimmed = newTag.trim();
    if (!trimmed || bulkTags.includes(trimmed)) return;
    bulkTags = [...bulkTags, trimmed];
    newTag = '';
  }

  function handleRemoveTag(tag: string) {
    bulkTags = bulkTags.filter((t) => t !== tag);
  }

  function handleTagKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  }

  // Spec 103 review U3: Esc with focus in the panel closes what is open here (editor, then
  // menu) — and never reaches the page's "clear selection" (which skips [data-bulk-panel]).
  function handlePanelKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    if (menuOpen) menuOpen = false;
    else openEditor = null;
  }

  // Spec 103 review U2: the × disappears with the reset, so focus moves to its chip.
  async function resetChip(key: ChipKey) {
    if (key === 'assign') bulkAssignMemberIds = [];
    else if (key === 'household') bulkHouseholdId = '';
    else {
      bulkTags = [];
      newTag = '';
    }
    await tick();
    panelEl?.querySelector<HTMLElement>(`[data-testid="bulk-chip-${key}"]`)?.focus();
  }

  // Flush any pending newTag into bulkTags (Spec 001 E1–E3, Spec 103 E7), then dispatch accept.
  // This ensures a tag typed but not confirmed via Plus/Enter is not silently lost.
  function acceptBulk() {
    bulkTags = mergeBulkTags(bulkTags, newTag);
    newTag = '';
    dispatch('accept');
  }

  // "a, b" for up to two values, "a +N" beyond that.
  function shorten(names: string[]): string {
    return names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1}`;
  }

  $: assignValue = shorten(
    bulkAssignMemberIds.map((id) => members.find((m) => m.id === id)?.name ?? id),
  );
  $: householdValue = (() => {
    const household = households.find((h) => h.id === bulkHouseholdId);
    return household ? household.name || household.id : bulkHouseholdId;
  })();
  $: tagsValue = shorten(bulkTags);

  $: chips = [
    {
      key: 'assign' as ChipKey,
      labelKey: 'pages.inbox.bulk_field_assigned_to',
      icon: Users,
      isSet: bulkAssignMemberIds.length > 0,
      value: assignValue,
    },
    {
      key: 'household' as ChipKey,
      labelKey: 'pages.inbox.bulk_field_household',
      icon: Home,
      isSet: bulkHouseholdId !== '',
      value: householdValue,
    },
    {
      key: 'tags' as ChipKey,
      labelKey: 'pages.inbox.bulk_field_tags',
      icon: Tag,
      isSet: bulkTags.length > 0,
      value: tagsValue,
    },
  ];

  // Drives the trigger: label ("N akzeptieren" vs "N ändern und akzeptieren") and, in edit mode,
  // whether it is enabled. A typed-but-unconfirmed tag counts — it is flushed on trigger.
  $: hasChanges = chips.some((c) => c.isSet) || newTag.trim() !== '';
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  bind:this={panelEl}
  data-testid="bulk-review-panel"
  data-bulk-panel
  on:keydown={handlePanelKeydown}
  class="p-4 flex flex-col gap-4 h-full overflow-y-auto"
>
  <h3 class="text-base font-semibold text-text">
    {$_('pages.inbox.bulk_panel_header', { values: { count } })}
  </h3>

  <div class="flex flex-wrap gap-2">
    {#each chips as chip (chip.key)}
      <span
        class="inline-flex max-w-full items-center rounded-full border text-sm font-medium transition-colors {chip.isSet
          ? 'border-accent/30 bg-accent-subtle text-accent'
          : 'border-border bg-surface text-text-muted hover:text-text hover:bg-surface-hover'}"
      >
        <button
          type="button"
          data-testid="bulk-chip-{chip.key}"
          aria-expanded={openEditor === chip.key}
          on:click={() => toggleEditor(chip.key)}
          class="inline-flex min-w-0 items-center gap-1.5 py-1.5 pl-3 {chip.isSet
            ? 'pr-1.5'
            : 'pr-3'} rounded-full"
        >
          <svelte:component this={chip.icon} class="w-4 h-4 shrink-0" strokeWidth={1.5} />
          <span class="truncate">
            {#if !chip.isSet}
              {$_(chip.labelKey)}
            {:else}
              {$_(`pages.inbox.bulk_chip_${chip.key}_set`, { values: { value: chip.value } })}
            {/if}
          </span>
        </button>
        {#if chip.isSet}
          <button
            type="button"
            data-testid="bulk-chip-{chip.key}-clear"
            aria-label={$_(`pages.inbox.bulk_chip_${chip.key}_reset`)}
            on:click={() => resetChip(chip.key)}
            class="mr-1 -my-1 shrink-0 p-1.5 rounded-full hover:bg-accent/15"
          >
            <X class="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        {/if}
      </span>
    {/each}
  </div>

  {#if openEditor === 'assign'}
    <div class="flex flex-col gap-1.5">
      {#each members as member (member.id)}
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={bulkAssignMemberIds.includes(member.id)}
            on:change={() => toggleMember(member.id)}
            class="accent-accent"
          />
          <span class="text-sm text-text">
            {member.name}
            {#if member.role}
              <span class="text-text-muted text-xs ml-1">({roleLabel(member.role)})</span>
            {/if}
          </span>
        </label>
      {:else}
        <p class="text-sm text-text-muted">{$_('upload.detail.no_members')}</p>
      {/each}
    </div>
  {:else if openEditor === 'household'}
    {#if households.length > 0}
      <select
        bind:value={bulkHouseholdId}
        aria-label={$_('pages.inbox.bulk_field_household')}
        class="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
      >
        <option value="">{$_('pages.inbox.bulk_household_placeholder')}</option>
        {#each households as household (household.id)}
          <option value={household.id}>
            {household.name || household.id}{household.type ? ` (${household.type})` : ''}
          </option>
        {/each}
      </select>
    {:else}
      <p class="text-sm text-text-muted text-center py-2 bg-surface rounded">
        {$_('upload.detail.no_households')}
      </p>
    {/if}
  {:else if openEditor === 'tags'}
    <div>
      <div class="flex gap-2">
        <input
          type="text"
          bind:value={newTag}
          aria-label={$_('pages.inbox.bulk_field_tags')}
          on:keydown={handleTagKeyDown}
          placeholder={$_('upload.detail.tag_placeholder')}
          class="flex-1 px-3 py-2 bg-surface border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
        />
        <button
          on:click={handleAddTag}
          type="button"
          class="px-3 py-2 bg-accent text-on-accent rounded text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus class="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
      {#if bulkTags.length > 0}
        <div class="flex flex-wrap gap-1.5 mt-2">
          {#each bulkTags as tag (tag)}
            <Badge variant="accent" removable on:remove={() => handleRemoveTag(tag)}>{tag}</Badge>
          {/each}
        </div>
      {/if}
    </div>
  {/if}

  <!-- Primary action: accept (applies any staged changes, then marks reviewed).
       A split-button caret opens a context menu for secondary/destructive options. -->
  <div class="pt-1 border-t border-border mt-auto">
    <div class="relative flex w-full">
      <button
        type="button"
        data-testid="bulk-accept"
        on:click={acceptBulk}
        disabled={loading || saving || (mode === 'edit' && !hasChanges)}
        class="flex-1 min-w-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-l-lg text-sm font-semibold bg-success text-white hover:bg-success/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {#if mode === 'edit'}
          <Pencil class="w-4 h-4 shrink-0" strokeWidth={2} />
        {:else}
          <CheckCircle class="w-4 h-4 shrink-0" strokeWidth={2} />
        {/if}
        <span class="truncate">
          {#if saving}
            {$_('pages.settings.saving')}
          {:else if mode === 'edit'}
            {$_('pages.inbox.bulk_change_n', { values: { count } })}
          {:else if hasChanges}
            {$_('pages.inbox.bulk_change_accept_n', { values: { count } })}
          {:else}
            {$_('pages.inbox.bulk_accept_n', { values: { count } })}
          {/if}
        </span>
      </button>
      <button
        type="button"
        data-testid="bulk-more-menu"
        on:click|stopPropagation={() => (menuOpen = !menuOpen)}
        disabled={loading || saving}
        class="shrink-0 px-2.5 flex items-center justify-center rounded-r-lg border-l border-white/25 bg-success text-white hover:bg-success/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label={$_('pages.inbox.bulk_more_options')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <ChevronDown class="w-4 h-4" strokeWidth={2} />
      </button>

      {#if menuOpen}
        <!-- Click-away backdrop -->
        <button
          type="button"
          class="fixed inset-0 z-20 cursor-default"
          tabindex="-1"
          aria-hidden="true"
          on:click={() => (menuOpen = false)}
        ></button>
        <div
          class="absolute right-0 bottom-full mb-1.5 z-30 min-w-[13rem] rounded-lg border border-border bg-surface shadow-lg overflow-hidden"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="bulk-delete"
            on:click={() => {
              menuOpen = false;
              dispatch('delete');
            }}
            disabled={loading || saving}
            class="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-error hover:bg-error/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
          >
            <Trash2 class="w-4 h-4 shrink-0" strokeWidth={1.5} />
            {$_('pages.inbox.bulk_delete_n', { values: { count } })}
          </button>
        </div>
      {/if}
    </div>
  </div>
</div>
